import { useEffect, useState, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowUp, MessageCircle, Bell, BellRing, ChevronRight, Sparkles, ShieldAlert } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { GlossaryText, fetchGlossaryTerms } from "@/lib/glossaryLink";

const TYPE_BADGE = {
  community_starter: { label: "Community Starter", cls: "bg-teal/10 text-teal" },
  editorial: { label: "Editorial", cls: "bg-plum/10 text-plum" },
  expert: { label: "Expert", cls: "bg-rose/40 text-plum" },
  member: { label: "Member", cls: "bg-rose-soft text-mutedplum" },
};

function timeAgo(iso) {
  if (!iso) return "";
  const d = (Date.now() - new Date(iso).getTime()) / 1000;
  if (d < 3600) return `${Math.max(1, Math.floor(d / 60))}m ago`;
  if (d < 86400) return `${Math.floor(d / 3600)}h ago`;
  return `${Math.floor(d / 86400)}d ago`;
}

export default function Thread() {
  const { categorySlug, threadSlug } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [reply, setReply] = useState("");
  const [notifying, setNotifying] = useState(false);
  const [error, setError] = useState("");
  const [posting, setPosting] = useState(false);
  const [genLoading, setGenLoading] = useState(false);
  const [terms, setTerms] = useState([]);
  useEffect(() => { fetchGlossaryTerms().then(setTerms); }, []);

  const load = useCallback(() => {
    api.get(`/threads/${categorySlug}/${threadSlug}`)
      .then((r) => { setData(r.data); setNotifying(!!r.data.thread?.notifying); })
      .catch(() => setNotFound(true));
  }, [categorySlug, threadSlug]);

  useEffect(() => { load(); }, [load]);

  const requireAuth = () => { if (!user) { navigate("/signin"); return false; } return true; };

  const vote = async (targetType, id, current) => {
    if (!requireAuth()) return;
    const url = targetType === "thread" ? `/threads/${id}/vote` : `/replies/${id}/vote`;
    try { await api.post(url, { value: 1 }); load(); } catch { setError("Couldn't register your vote. Please try again."); }
  };

  const toggleNotify = async () => {
    if (!requireAuth()) return;
    try { const { data: r } = await api.post(`/threads/${data.thread.thread_id}/notify`); setNotifying(r.notifying); } catch {}
  };

  const submitReply = async (e) => {
    e.preventDefault();
    if (!requireAuth()) return;
    setPosting(true); setError("");
    try {
      const { data: r } = await api.post(`/threads/${data.thread.thread_id}/replies`, { body: reply });
      setReply("");
      if (r.status === "pending") setError("Thanks! Your reply is awaiting moderation and will appear shortly.");
      load();
    } catch (err) { setError(formatApiError(err.response?.data?.detail)); }
    finally { setPosting(false); }
  };

  const generateSummary = async () => {
    setGenLoading(true);
    try { await api.post(`/threads/${data.thread.thread_id}/summary`); load(); }
    catch (err) { setError(formatApiError(err.response?.data?.detail)); }
    finally { setGenLoading(false); }
  };

  if (notFound) return <div className="max-w-3xl mx-auto py-24 px-6 text-center text-mutedplum">Discussion not found. <Link to="/community" className="text-teal font-semibold">Back to Community</Link></div>;
  if (!data) return <div className="py-24 text-center text-mutedplum">Loading…</div>;

  const { thread, replies, category } = data;
  const badge = TYPE_BADGE[thread.author_type] || TYPE_BADGE.member;
  const isMod = user && (user.role === "admin" || user.role === "moderator");

  return (
    <div className="mx-auto max-w-3xl px-4 sm:px-6 py-10">
      <nav className="text-sm flex items-center gap-1.5 text-mutedplum mb-5 flex-wrap">
        <Link to="/community" className="hover:text-teal">Community</Link><ChevronRight className="h-4 w-4" />
        <Link to={`/community/${categorySlug}`} className="hover:text-teal">{category?.name}</Link>
      </nav>

      <article className="bg-white rounded-3xl border border-rose/30 shadow-card p-6 sm:p-8">
        <div className="flex items-center gap-2 mb-3">
          <span className={`text-xs font-semibold px-3 py-1 rounded-full ${badge.cls}`}>{badge.label}</span>
          {thread.content_origin === "ai_assisted" && (
            <span className="text-xs text-mutedplum inline-flex items-center gap-1"><Sparkles className="h-3.5 w-3.5" /> AI-started, human-led</span>
          )}
        </div>
        <h1 className="font-display text-3xl sm:text-4xl text-plum leading-tight">{thread.title}</h1>
        <div className="mt-3 flex items-center gap-3 text-sm text-mutedplum">
          <span className="font-medium text-plum">{thread.author_name}</span>
          <span>·</span><span>{timeAgo(thread.created_at)}</span>
        </div>
        <GlossaryText text={thread.body} terms={terms} className="mt-5 text-plum-ink/90 text-[17px] leading-relaxed whitespace-pre-wrap" />

        <div className="mt-6 flex items-center gap-3 flex-wrap">
          <button onClick={() => vote("thread", thread.thread_id)} data-testid="thread-upvote"
            className="inline-flex items-center gap-2 rounded-full border-2 border-rose/50 px-4 py-2 text-sm font-semibold text-plum hover:border-teal hover:text-teal transition-colors">
            <ArrowUp className="h-4 w-4" /> {thread.upvotes}
          </button>
          <span className="inline-flex items-center gap-2 text-sm text-mutedplum"><MessageCircle className="h-4 w-4" /> {thread.reply_count} replies</span>
          <button onClick={toggleNotify} data-testid="notify-btn"
            className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-colors ml-auto ${notifying ? "bg-teal text-white" : "border-2 border-rose/50 text-plum hover:border-teal hover:text-teal"}`}>
            {notifying ? <BellRing className="h-4 w-4" /> : <Bell className="h-4 w-4" />}
            {notifying ? "We'll notify you" : "Notify me of replies"}
          </button>
        </div>
      </article>

      {/* Community consensus */}
      {thread.ai_summary ? (
        <div className="mt-6 rounded-3xl bg-teal/8 border border-teal/25 p-6" data-testid="ai-summary">
          <h2 className="font-display text-xl text-teal flex items-center gap-2"><Sparkles className="h-5 w-5" /> Community consensus</h2>
          <p className="mt-3 text-plum-ink/90 whitespace-pre-wrap leading-relaxed">{thread.ai_summary}</p>
          <p className="mt-4 text-sm text-mutedplum flex items-start gap-1.5"><ShieldAlert className="h-4 w-4 mt-0.5 shrink-0" /> {thread.ai_summary_disclaimer || "This is community conversation, not medical advice. Please consult a professional."}</p>
        </div>
      ) : isMod && thread.reply_count >= 2 ? (
        <button onClick={generateSummary} disabled={genLoading} data-testid="generate-summary-btn"
          className="mt-6 inline-flex items-center gap-2 rounded-full bg-plum px-6 py-3 font-semibold text-cream hover:bg-plum-dark transition-colors disabled:opacity-60">
          <Sparkles className="h-4 w-4" /> {genLoading ? "Summarising…" : "Generate Community consensus"}
        </button>
      ) : null}

      {/* Replies */}
      <section className="mt-8">
        <h2 className="font-display text-2xl text-plum mb-4">{replies.length} {replies.length === 1 ? "reply" : "replies"}</h2>
        <div className="space-y-4" data-testid="replies-list">
          {replies.map((r) => {
            const rb = TYPE_BADGE[r.author_type] || TYPE_BADGE.member;
            return (
              <div key={r.reply_id} className="bg-white rounded-2xl border border-rose/30 p-5">
                <div className="flex items-center gap-2 mb-2">
                  <span className="font-semibold text-plum">{r.author_name}</span>
                  <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${rb.cls}`}>{rb.label}</span>
                  <span className="text-xs text-mutedplum">· {timeAgo(r.created_at)}</span>
                </div>
                <p className="text-plum-ink/90 whitespace-pre-wrap leading-relaxed">{r.body}</p>
                <button onClick={() => vote("reply", r.reply_id)} data-testid={`reply-upvote-${r.reply_id}`}
                  className="mt-3 inline-flex items-center gap-1.5 text-sm text-mutedplum hover:text-teal transition-colors">
                  <ArrowUp className="h-4 w-4" /> {r.upvotes}
                </button>
              </div>
            );
          })}
        </div>

        {/* Reply form */}
        <form onSubmit={submitReply} className="mt-6 bg-white rounded-3xl border border-rose/30 shadow-card p-6" data-testid="reply-form">
          <h3 className="font-display text-xl text-plum mb-3">Join the conversation</h3>
          {user ? (
            <>
              <textarea value={reply} onChange={(e) => setReply(e.target.value)} required rows={4}
                placeholder="Share your experience, a question, or some encouragement…" data-testid="reply-input"
                className="w-full rounded-2xl border-2 border-rose/50 bg-cream px-4 py-3 outline-none focus:border-teal transition-colors resize-none" />
              {error && <p className="mt-2 text-sm text-teal" data-testid="reply-message">{error}</p>}
              <button type="submit" disabled={posting} data-testid="reply-submit"
                className="mt-3 rounded-full bg-teal px-7 py-3 font-semibold text-white hover:bg-teal-dark transition-colors disabled:opacity-60">
                {posting ? "Posting…" : "Post reply"}
              </button>
            </>
          ) : (
            <p className="text-mutedplum"><Link to="/signin" className="text-teal font-semibold">Sign in</Link> or <Link to="/join" className="text-teal font-semibold">join the Circle</Link> to reply. It's free.</p>
          )}
        </form>
      </section>
    </div>
  );
}
